import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Button,
  Typography,
  Chip,
  Tab,
  Tabs,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
  Dialog,
  DialogContent,
  Drawer,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ErpDataGrid, { GeneralItemsGridFrame } from '../../components/common/ErpDataGrid';
import ListDetailOpenModeBar from '../../components/common/ListDetailOpenModeBar';
import ListRowActionsBar from '../../components/common/ListRowActionsBar';
import useListDetailOpenMode from '../../hooks/useListDetailOpenMode';
import TimesheetForm from './TimesheetForm';
import {
  Add as AddIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { standardAuditColumns } from '../../utils/auditGridColumns';
import { copyAppPathToClipboard, openAppPathInNewTab } from '../../utils/listScreenHelpers';

const pendingListScrollbarSx = {
  maxHeight: 'min(72vh, 720px)',
  overflow: 'auto',
  scrollbarGutter: 'stable',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': { width: 11, height: 11 },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 8,
    backgroundColor: (theme) => alpha(theme.palette.text.secondary, 0.35),
  },
  '&::-webkit-scrollbar-track': {
    borderRadius: 8,
    backgroundColor: (theme) => alpha(theme.palette.text.secondary, 0.08),
  },
};

const TimesheetsList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { mode, persistMode, popupId, setPopupId, sideId, setSideId, openRow } = useListDetailOpenMode('timesheets');
  const [activeTab, setActiveTab] = useState(0);
  const [timesheets, setTimesheets] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10, total: 0 });

  useEffect(() => {
    if (activeTab === 0) fetchMyTimesheets();
    else if (activeTab === 1) fetchPendingApprovals();
    else fetchAllTimesheets();
  }, [activeTab, pagination.page, pagination.pageSize]);

  const fetchMyTimesheets = async () => {
    setLoading(true);
    try {
      const r = await api.get('/timesheets/my-timesheets', {
        params: { page: pagination.page + 1, limit: pagination.pageSize },
      });
      setTimesheets(r.data.data);
      setPagination((p) => ({ ...p, total: r.data.pagination?.total || 0 }));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    setLoading(true);
    try {
      const r = await api.get('/timesheets/pending-approvals');
      setPendingApprovals(r.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTimesheets = async () => {
    setLoading(true);
    try {
      const r = await api.get('/timesheets', {
        params: { page: pagination.page + 1, limit: pagination.pageSize },
      });
      setTimesheets(r.data.data);
      setPagination((p) => ({ ...p, total: r.data.pagination?.total || 0 }));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.post(`/timesheets/${id}/approve`);
      toast.success('Timesheet approved');
      fetchPendingApprovals();
    } catch (e) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await api.post(`/timesheets/${id}/reject`, { rejection_reason: reason });
      toast.success('Timesheet rejected');
      fetchPendingApprovals();
    } catch (e) {
      toast.error('Failed to reject');
    }
  };

  const statusColors = {
    draft: '#6b7280',
    submitted: '#f59e0b',
    approved: '#10b981',
    rejected: '#ef4444',
    processed: '#3b82f6',
  };

  const columns = [
    {
      field: 'employee',
      headerName: 'Employee',
      flex: 1,
      minWidth: 200,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>
            {p.row.Employee?.Contact?.first_name?.[0]}
            {p.row.Employee?.Contact?.last_name?.[0]}
          </Avatar>
          <Typography variant="body2">
            {p.row.Employee?.Contact?.first_name} {p.row.Employee?.Contact?.last_name}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'period',
      headerName: 'Period',
      width: 180,
      renderCell: (p) =>
        `${new Date(p.row.period_start).toLocaleDateString()} - ${new Date(p.row.period_end).toLocaleDateString()}`,
    },
    { field: 'timesheet_type', headerName: 'Type', width: 100 },
    { field: 'total_hours', headerName: 'Hours', width: 80 },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (p) => (
        <Chip
          label={p.value?.toUpperCase()}
          size="small"
          sx={{
            bgcolor: `${statusColors[p.value]}20`,
            color: statusColors[p.value],
            fontWeight: 500,
            fontSize: '0.7rem',
          }}
        />
      ),
    },
    ...standardAuditColumns(),
    {
      field: 'actions',
      headerName: 'Actions',
      width: 240,
      sortable: false,
      renderCell: (p) => {
        const path = `/timesheets/${p.row.id}`;
        return (
          <ListRowActionsBar
            menuItems={[
              {
                label: 'Open in new tab',
                icon: <OpenInNewIcon fontSize="small" />,
                onClick: () => openAppPathInNewTab(path),
              },
              {
                label: 'Copy link',
                icon: <CopyIcon fontSize="small" />,
                onClick: () => copyAppPathToClipboard(path),
              },
            ]}
          >
            {hasPermission('timesheets_modify') && (
              <Tooltip title="Edit timesheet">
                <IconButton size="small" color="primary" onClick={() => navigate(path)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="View">
              <IconButton size="small" onClick={() => navigate(path)}>
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open for print or Save as PDF">
              <IconButton size="small" color="secondary" onClick={() => openAppPathInNewTab(path)}>
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ListRowActionsBar>
        );
      },
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Timesheets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track and manage time entries
          </Typography>
        </Box>
        {hasPermission('timesheets_add') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/timesheets/new')}
            sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
          >
            New Timesheet
          </Button>
        )}
      </Box>

      <Card sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 2, pt: 2, pb: 0 }}>
          <ListDetailOpenModeBar value={mode} onChange={persistMode} />
        </Box>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="My Timesheets" />
          {hasPermission('timesheets_approve') && (
            <Tab label={`Pending Approvals (${pendingApprovals.length})`} />
          )}
          {hasPermission('timesheets_view') && <Tab label="All Timesheets" />}
        </Tabs>
        <Box sx={{ p: 2 }}>
          {activeTab === 1 ? (
            loading ? (
              <CircularProgress />
            ) : pendingApprovals.length === 0 ? (
              <Typography textAlign="center" py={4} color="text.secondary">
                No pending approvals
              </Typography>
            ) : (
              <Paper
                variant="outlined"
                sx={(theme) => ({
                  ...pendingListScrollbarSx,
                  p: 1,
                  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                  borderColor: 'divider',
                })}
              >
                <List disablePadding>
                  {pendingApprovals.map((t) => {
                    const path = `/timesheets/${t.id}`;
                    return (
                      <ListItem
                        key={t.id}
                        divider
                        secondaryAction={
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.25, pr: 0.5 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Tooltip title="View">
                              <IconButton size="small" onClick={() => navigate(path)}>
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Open for print or Save as PDF">
                              <IconButton size="small" color="secondary" onClick={() => openAppPathInNewTab(path)}>
                                <PdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Approve">
                              <IconButton size="small" color="success" onClick={() => handleApprove(t.id)}>
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton size="small" color="error" onClick={() => handleReject(t.id)}>
                                <RejectIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <ListRowActionsBar
                              menuItems={[
                                {
                                  label: 'Open in new tab',
                                  icon: <OpenInNewIcon fontSize="small" />,
                                  onClick: () => openAppPathInNewTab(path),
                                },
                                {
                                  label: 'Copy link',
                                  icon: <CopyIcon fontSize="small" />,
                                  onClick: () => copyAppPathToClipboard(path),
                                },
                              ]}
                            />
                          </Box>
                        }
                      >
                        <ListItemAvatar>
                          <Avatar>
                            {t.Employee?.Contact?.first_name?.[0]}
                            {t.Employee?.Contact?.last_name?.[0]}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${t.Employee?.Contact?.first_name} ${t.Employee?.Contact?.last_name}`}
                          secondary={`${new Date(t.period_start).toLocaleDateString()} - ${new Date(
                            t.period_end
                          ).toLocaleDateString()} • ${t.total_hours} hours`}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            )
          ) : (
            <GeneralItemsGridFrame>
              <ErpDataGrid
                listApplet
                columnOrderStorageKey="general-items-timesheets"
                rows={timesheets}
                columns={columns}
                loading={loading}
                paginationMode="server"
                rowCount={pagination.total}
                paginationModel={{ page: pagination.page, pageSize: pagination.pageSize }}
                onPaginationModelChange={(m) => setPagination({ ...pagination, ...m })}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                autoHeight={false}
                sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
                onRowClick={(p) => openRow(p.row, (r) => `/timesheets/${r.id}`)}
              />
            </GeneralItemsGridFrame>
          )}
        </Box>
      </Card>

      <Drawer
        anchor="right"
        open={mode === 'side' && Boolean(sideId)}
        onClose={() => setSideId(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 640, md: 800 }, maxWidth: '100vw' } }}
      >
        <Box sx={{ height: '100%', overflow: 'auto', p: { xs: 1.5, sm: 2 } }}>
          {sideId ? (
            <TimesheetForm detailId={sideId} embedded onCloseEmbedded={() => setSideId(null)} />
          ) : null}
        </Box>
      </Drawer>

      <Dialog fullWidth maxWidth="lg" open={Boolean(popupId)} onClose={() => setPopupId(null)} scroll="paper">
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          {popupId ? (
            <TimesheetForm detailId={popupId} embedded onCloseEmbedded={() => setPopupId(null)} />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TimesheetsList;
